/**
 * The whole revive screen as a pure function of props (`plan.md` WI-3). No
 * data fetching, no mutation, no URL reads or writes, no `showApiError`
 * call. `ReviveSheetContainer` decides `block` and `inlineNote`; this
 * component only renders what it is handed.
 *
 * The picker branch (D-b, D-c) renders nothing else in the body , no
 * elimination line, no counts, no budget, no stepper, and no `block` note
 * either: `block` is computed from `entryStatus: selected?.status ?? null`,
 * which is also `null` while two or more eliminated entries exist and none
 * is chosen yet, so its `noEntry` note ("no eliminated entry to bring
 * back") would be false while the picker is showing exactly those entries.
 * The note belongs to the resolved-entry view, so it renders there only.
 */
import { Button, Dialog, InlineBanner, Skeleton } from "@sportsmet/ui";
import type { JSX } from "react";

import { LivesGrantedStepper } from "./LivesGrantedStepper";
import { PickRequirementStepper } from "./PickRequirementStepper";
import { ReleaseTeamsList } from "./ReleaseTeamsList";
import {
  eliminationLine,
  ENTRY_PICKER_PROMPT,
  REVIVE_SHEET_CANCEL_LABEL,
  reviveBudgetLine,
  reviveCountsLine,
  revivePrimaryLabel,
  reviveSheetTitle,
  type typeReviveBlock,
} from "./reviveSheetModel";

export type typeReviveSheetEntryOption = { entryId: string; label: string };

export type typeReviveSheetProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  name: string;
  isLoading: boolean;
  isSubmitting: boolean;
  entryOptions: readonly typeReviveSheetEntryOption[];
  selectedEntryId: string | null;
  onSelectEntry: (entryId: string) => void;
  roundLabel: string | null;
  usedThisContest: number;
  usedThisSeason: number;
  revivesAllowed: "unlimited" | "limited" | null;
  revivesAllowedCount: number | null | undefined;
  block: typeReviveBlock;
  inlineNote: string | null;
  pickRequirement: number;
  onPickRequirementChange: (next: number) => void;
  /** ISSUE-1326. Lives the entry comes back with. */
  livesGranted: number;
  maxLives: number | null;
  onLivesGrantedChange: (next: number) => void;
  /** ISSUE-1326. The entry's used teams, and which of them it gets back. */
  releasableTeams: readonly { teamId: string; name: string }[];
  releaseTeamIds: readonly string[];
  onReleaseTeamIdsChange: (next: string[]) => void;
  onConfirm: () => void;
};

const DISABLING_BLOCK_KINDS = new Set<typeReviveBlock["kind"]>([
  "contestConfirmed",
  "contestNotRunning",
  "budgetSpent",
]);

const HIDING_BLOCK_KINDS = new Set<typeReviveBlock["kind"]>([
  "noEntry",
  "alreadyBack",
]);

export function ReviveSheet(props: typeReviveSheetProps): JSX.Element {
  const {
    open,
    onOpenChange,
    name,
    isLoading,
    isSubmitting,
    entryOptions,
    selectedEntryId,
    onSelectEntry,
    roundLabel,
    usedThisContest,
    usedThisSeason,
    revivesAllowed,
    revivesAllowedCount,
    block,
    inlineNote,
    pickRequirement,
    onPickRequirementChange,
    livesGranted,
    maxLives,
    onLivesGrantedChange,
    releasableTeams,
    releaseTeamIds,
    onReleaseTeamIdsChange,
    onConfirm,
  } = props;

  const showPicker = entryOptions.length > 1 && selectedEntryId === null;
  const hidePrimary = HIDING_BLOCK_KINDS.has(block.kind);
  const hideStepper = HIDING_BLOCK_KINDS.has(block.kind);
  const primaryDisabled = isSubmitting || DISABLING_BLOCK_KINDS.has(block.kind);

  function renderBody(): JSX.Element {
    if (isLoading) {
      return (
        <div data-testid="revive-loading">
          <Skeleton shape="text" lines={3} label={`Loading ${name}'s entry`} />
        </div>
      );
    }

    if (showPicker) {
      return (
        <div className="flex flex-col gap-3">
          <p className="text-body text-ink">{ENTRY_PICKER_PROMPT}</p>
          <div role="radiogroup" className="flex flex-col gap-2">
            {entryOptions.map((option) => (
              <label
                key={option.entryId}
                data-testid={`revive-entry-${option.entryId}`}
                className="flex items-center gap-3 rounded-md border border-line p-3"
              >
                <input
                  type="radio"
                  name="revive-entry"
                  value={option.entryId}
                  onChange={() => {
                    onSelectEntry(option.entryId);
                  }}
                />
                <span className="text-body text-ink">{option.label}</span>
              </label>
            ))}
          </div>
        </div>
      );
    }

    return (
      <div className="flex flex-col gap-4">
        <p data-testid="revive-elimination" className="text-body text-ink">
          {eliminationLine({ roundLabel })}
        </p>
        <p data-testid="revive-counts" className="text-body text-ink">
          {reviveCountsLine({ usedThisContest, usedThisSeason })}
        </p>
        <p data-testid="revive-budget" className="text-body text-ink">
          {reviveBudgetLine({
            revivesAllowed: revivesAllowed ?? "unlimited",
            revivesAllowedCount,
            usedThisContest,
          })}
        </p>
        {!hideStepper ? (
          <PickRequirementStepper
            name={name}
            value={pickRequirement}
            onChange={onPickRequirementChange}
            disabled={isSubmitting}
          />
        ) : null}
        {!hideStepper ? (
          <LivesGrantedStepper
            name={name}
            value={livesGranted}
            maxLives={maxLives}
            onChange={onLivesGrantedChange}
            disabled={isSubmitting}
          />
        ) : null}
        {!hideStepper ? (
          <ReleaseTeamsList
            name={name}
            teams={releasableTeams}
            selectedTeamIds={releaseTeamIds}
            onChange={onReleaseTeamIdsChange}
            disabled={isSubmitting}
          />
        ) : null}
        {block.kind !== "none" ? (
          <div data-testid="revive-block-note">
            <InlineBanner variant="warning" message={block.note} />
          </div>
        ) : null}
        {inlineNote !== null ? (
          <div data-testid="revive-inline-note">
            <InlineBanner variant="warning" message={inlineNote} />
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={reviveSheetTitle({ name })}
      autoFocus={false}
      footer={
        <>
          <Button
            variant="secondary"
            onClick={() => {
              onOpenChange(false);
            }}
          >
            {REVIVE_SHEET_CANCEL_LABEL}
          </Button>
          {!hidePrimary ? (
            <Button
              variant="primary"
              data-testid="revive-confirm"
              disabled={primaryDisabled}
              loading={isSubmitting}
              onClick={onConfirm}
            >
              {revivePrimaryLabel({ name })}
            </Button>
          ) : null}
        </>
      }
    >
      {renderBody()}
    </Dialog>
  );
}
